import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    UserRole,
    ViewConfig,
    ViewConfigService,
    ViewContext,
    ViewToggleConfig,
    ViewType,
} from '../models/view-config.interface';
import { ConsignorViewConfigService } from './view-configs/consignor/index';
import { CarrierViewConfigService } from './view-configs/carrier/index';
import { ConsigneeViewConfigService } from './view-configs/consignee/index';
import { AppStore } from '@app/stores/appStore';

/**
 * Main View Configuration Service
 *
 * This service acts as an index/aggregator for all role-specific view configurations.
 * It combines configurations from:
 * - ConsignorViewConfigService (folders, delivery notes)
 * - CarrierViewConfigService (folders, delivery notes, tours)
 * - ConsigneeViewConfigService (folders, delivery notes, tours, notifications)
 *
 * Usage:
 * - Inject this service to access all view configurations
 * - The service automatically combines all role-specific configs
 * - Individual role services can be modified independently
 */
@Injectable({
    providedIn: 'root',
})
export class ViewConfigurationService implements ViewConfigService {
    private viewConfigs: Map<string, ViewConfig> = new Map();
    private appStore = inject(AppStore);

    constructor(
        private consignorViewConfigService: ConsignorViewConfigService,
        private carrierViewConfigService: CarrierViewConfigService,
        private consigneeViewConfigService: ConsigneeViewConfigService
    ) {
        this.initializeConfigurations();
    }

    private initializeConfigurations(): void {
        // Combine all role-specific configurations
        const consignorConfigs =
            this.consignorViewConfigService.getViewConfigs();
        const carrierConfigs = this.carrierViewConfigService.getViewConfigs();
        const consigneeConfigs =
            this.consigneeViewConfigService.getViewConfigs();

        // Add all configurations to the main map
        consignorConfigs.forEach((config, key) => {
            this.viewConfigs.set(key, config);
        });

        carrierConfigs.forEach((config, key) => {
            this.viewConfigs.set(key, config);
        });

        consigneeConfigs.forEach((config, key) => {
            this.viewConfigs.set(key, config);
        });
    }

    // Interface implementation methods
    getViewConfig(
        userRole: UserRole,
        viewType: ViewType
    ): ViewConfig | undefined {
        const key = `${userRole}-${viewType}`;
        const config = this.viewConfigs.get(key);
        return config;
    }

    getAvailableViews(userRole: UserRole): ViewConfig[] {
        const views: ViewConfig[] = [];
        this.viewConfigs.forEach(config => {
            if (config.userRole === userRole) {
                views.push(config);
            }
        });
        return views;
    }

    getViewToggleConfig(userRole: UserRole): ViewToggleConfig {
        const availableViews = this.getAvailableViews(userRole);
        return {
            views: availableViews.map(view => ({
                id: view.id,
                name: view.name,
                translationKey: view.translationKey,
                type: view.type,
                icon: this.getViewIcon(view.type),
                enabled: true,
                visible: this.getViewVisibility(view.type, userRole),
            })),
            defaultView: availableViews[0]?.type || 'deliverynotes',
            toggleLabels: {
                ecmr: 'ECMR',
                folders: 'Folders',
                deliverynotes: 'Delivery Notes',
                tours: 'Tours',
                notifications: 'Notifications',
                bundlegroups: 'Bundle Groups',
            },
            toggleTranslationKeys: {
                ecmr: 'OUTGOING-GOODS.TOGGLER.ECMR',
                folders: 'OUTGOING-GOODS.TOGGLER.FOLDERS',
                deliverynotes: 'OUTGOING-GOODS.TOGGLER.DELIVERY-NOTES',
                tours: 'OUTGOING-GOODS.TOGGLER.TOURS',
                notifications: 'OUTGOING-GOODS.TOGGLER.NOTIFICATIONS',
                bundlegroups: 'OUTGOING-GOODS.TOGGLER.BUNDLE-GROUPS',
            },
        };
    }

    private getViewIcon(viewType: ViewType): string {
        switch (viewType) {
            case 'ecmr':
                return 'description';
            case 'folders':
                return 'folder';
            case 'deliverynotes':
                return 'note';
            case 'tours':
                return 'route';
            case 'notifications':
                return 'message';
            case 'bundlegroups':
                return 'inventory_2';
            default:
                return 'view_list';
        }
    }

    private getViewVisibility(
        viewType: ViewType,
        userRole: UserRole
    ): boolean | ((context?: any) => boolean) {
        // Hide tours view when in tour plan mode for carriers
        if (viewType === 'tours' && userRole === 'carrier') {
            return (context?: any) => !this.appStore.isTourPlanMode();
        }

        // Default visibility is true
        return true;
    }

    updateViewConfig(config: ViewConfig): void {
        this.viewConfigs.set(config.id, config);
    }

    executeViewAction(
        userRole: UserRole,
        viewType: ViewType,
        actionId: string,
        context: ViewContext
    ): Observable<any> | Promise<any> {
        const config = this.getViewConfig(userRole, viewType);
        if (!config) {
            throw new Error(
                `View configuration not found for ${userRole}-${viewType}`
            );
        }

        const action = config.headerActionConfig?.headerActions?.find(
            a => a.id === actionId
        );

        if (!action) {
            throw new Error(
                `Action ${actionId} not found in ${userRole}-${viewType}`
            );
        }

        const result = action.handler(
            context.selectedItems?.[0],
            context.selectedItems
        );
        return result || Promise.resolve();
    }

    validateViewConfig(config: ViewConfig): boolean {
        return !!(
            config.id &&
            config.type &&
            config.userRole &&
            config.apiConfig
        );
    }

    // Helper methods for action handling
    private async handleQuotaCheck(
        quotaType: string,
        orgSiteKey: string
    ): Promise<boolean> {
        // TODO: Implement quota checking logic
        return true;
    }

    private async handleConfirmation(
        message: string,
        title?: string
    ): Promise<boolean> {
        // TODO: Implement confirmation dialog
        return true;
    }

    /**
     * Get all view configurations (for debugging/testing)
     */
    getAllConfigs(): Map<string, ViewConfig> {
        return new Map(this.viewConfigs);
    }

    /**
     * Refresh configurations from all role services
     */
    refreshConfigurations(): void {
        this.viewConfigs.clear();
        this.initializeConfigurations();
    }
}
